import { useAuthorizedRequest } from '@/hooks/useRequest';
import { apiRequest } from '@/lib/api';
import type { Subject, SubjectAttributes } from '@/lib/Subject';
import { getSubjectPrettyName } from '@/lib/Subject';
import { t } from '@/lib/i18n';
import { Box, Button, CloseButton, Drawer, HStack, Input, Portal, Skeleton, Table, Text, Tooltip, VStack } from '@chakra-ui/react';
import { useAuth0 } from '@auth0/auth0-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

function jsonFetcher(url: string, token: string) {
    return fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
}

interface SubjectFormDrawerProps {
    title: string;
    initial: Partial<SubjectAttributes>;
    open: boolean;
    onClose: () => void;
    onSave: (attrs: SubjectAttributes) => Promise<void>;
}

function SubjectFormDrawer({ title, initial, open, onClose, onSave }: SubjectFormDrawerProps) {
    const [name, setName] = useState(initial.name ?? '');
    const [deleted, setDeleted] = useState(initial.deleted ?? false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    async function handleSubmit() {
        if (!name.trim()) { setError(t('subjects.name_required')); return; }
        setSubmitting(true);
        setError('');
        try {
            await onSave({ name: name.trim(), deleted });
            onClose();
        } catch (e) {
            setError(e instanceof Error ? e.message : t('error.unknown'));
        } finally {
            setSubmitting(false);
        }
    }

    function handleOpenChange(e: { open: boolean }) {
        if (!e.open) {
            setName(initial.name ?? '');
            setDeleted(initial.deleted ?? false);
            setError('');
            onClose();
        }
    }

    return (
        <Drawer.Root open={open} onOpenChange={handleOpenChange} placement="end">
            <Portal>
                <Drawer.Backdrop />
                <Drawer.Positioner>
                    <Drawer.Content>
                        <Drawer.Header>
                            <Drawer.Title>{title}</Drawer.Title>
                        </Drawer.Header>
                        <Drawer.Body>
                            <VStack gap={4} align="stretch">
                                {error && <Text color="red.500" fontSize="sm">{error}</Text>}
                                <Box><Text fontSize="sm" mb="1">{t('label.name')} *</Text>
                                    <Input size="sm" value={name} onChange={e => setName(e.target.value)} placeholder={t('placeholder.subject_name')} />
                                </Box>
                                <Box>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                                        <input type="checkbox" checked={deleted} onChange={e => setDeleted(e.target.checked)} />
                                        {t('label.deleted')}
                                    </label>
                                </Box>
                            </VStack>
                        </Drawer.Body>
                        <Drawer.Footer>
                            <HStack>
                                <Button size="sm" onClick={handleSubmit} loading={submitting}>{t('action.save')}</Button>
                                <Button size="sm" variant="ghost" onClick={onClose}>{t('action.cancel')}</Button>
                            </HStack>
                        </Drawer.Footer>
                        <Drawer.CloseTrigger asChild>
                            <CloseButton size="xs" variant="solid" />
                        </Drawer.CloseTrigger>
                    </Drawer.Content>
                </Drawer.Positioner>
            </Portal>
        </Drawer.Root>
    );
}

export function Subjects() {
    const navigate = useNavigate();
    const { getAccessTokenSilently } = useAuth0();
    const [filter, setFilter] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [editSubject, setEditSubject] = useState<Subject | null>(null);

    const { data, error, isLoading, isAuthenticated, mutate } =
        useAuthorizedRequest<{ subjects: Subject[] }>('/subjects', jsonFetcher);

    const subjects = useMemo(() => {
        return (data?.subjects ?? [])
            .filter(t => !filter || t.name.toLowerCase().includes(filter.toLowerCase()) || t.id.includes(filter))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [data, filter]);

    async function handleCreate(attrs: SubjectAttributes) {
        const token = await getAccessTokenSilently();
        await apiRequest('POST', '/subjects', token, attrs);
        await mutate();
    }

    async function handleEdit(attrs: SubjectAttributes) {
        if (!editSubject) return;
        const token = await getAccessTokenSilently();
        await apiRequest('PUT', `/subjects/${editSubject.id}`, token, attrs);
        await mutate();
    }

    if (!isAuthenticated) return <div>{t('label.needs_auth')}</div>;
    if (error) return <div>{t('label.error')} {t('subjects.title')}: {error.message}</div>;

    return (
        <VStack gap={4} align="stretch">
            <HStack justify="space-between">
                <Input placeholder={t('subjects.search')} variant="subtle" flex="1" onChange={(e) => setFilter(e.target.value)} />
                <Button size="sm" onClick={() => setCreateOpen(true)}>{t('subjects.new')}</Button>
            </HStack>
            <Table.Root variant="line" showColumnBorder interactive size="sm">
                <Table.Header>
                    <Table.Row>
                        <Table.ColumnHeader>{t('label.name')}</Table.ColumnHeader>
                        <Table.ColumnHeader>{t('label.actions')}</Table.ColumnHeader>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {isLoading ? (
                        [...Array(5)].map((_, i) => (
                            <Table.Row key={i}>
                                {[...Array(2)].map((_, j) => (
                                    <Table.Cell key={j}><Skeleton height="20px" /></Table.Cell>
                                ))}
                            </Table.Row>
                        ))
                    ) : subjects.length === 0 ? (
                        <Table.Row>
                            <Table.Cell colSpan={2} style={{ textAlign: 'center' }}>
                                <Text color="gray.500">{t('label.no_data')}</Text>
                            </Table.Cell>
                        </Table.Row>
                    ) : subjects.map((subject) => (
                        <Table.Row key={subject.id}>
                            <Table.Cell>{getSubjectPrettyName(subject.name)}</Table.Cell>
                            <Table.Cell>
                                <Tooltip.Root>
                                    <Tooltip.Trigger asChild>
                                        <Button size="xs" variant="ghost" onClick={() => setEditSubject(subject)} aria-label={t('action.edit')}>✏️</Button>
                                    </Tooltip.Trigger>
                                    <Tooltip.Positioner>
                                        <Tooltip.Content>{t('action.edit')}</Tooltip.Content>
                                    </Tooltip.Positioner>
                                </Tooltip.Root>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table.Root>

            <SubjectFormDrawer
                title={t('subjects.new').replace('+ ', '')}
                initial={{}}
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onSave={handleCreate}
            />

            {editSubject && (
                <SubjectFormDrawer
                    title={`${t('subjects.edit')}: ${getSubjectPrettyName(editSubject.name)}`}
                    initial={{ name: editSubject.name, deleted: editSubject.deleted }}
                    open={!!editSubject}
                    onClose={() => setEditSubject(null)}
                    onSave={handleEdit}
                />
            )}
        </VStack>
    );
}
